/**
 * Documents section for the merchant-detail Review tab.
 *
 * Extracted from `AdminMerchantDetail.jsx` in Brief 3.5 PR 6.1 — pure
 * mechanical refactor of code that landed in PR 4 (Workstream 2).
 * Feb-25 follow-up: split out the extraction-details + pill components
 * into their own files so this module stays focused on the table shell
 * and row rendering.
 *
 * Contents (after the split):
 *   • DocumentsTable          — the filter + table shell with row-click
 *                                 inline expansion.
 *
 * Co-located siblings:
 *   • DocExtractionDetails.jsx   — InlineExtractionDetails + ExtractionField
 *   • DocPills.jsx               — DocStatePill + ExtractionPill
 *   • DocReviewActions.jsx       — Reject / Withdraw / AI-check / Mark-verified
 *   • AdminUploadButton.jsx      — admin upload-on-behalf-of-merchant
 */
import React, { useMemo, useState } from "react";
import {
  AlertTriangle, ChevronRight, Eye, FileText, Loader2, Search,
} from "lucide-react";
import api from "../../../lib/api";
import { plainDocKey, plainFindingSeverity } from "../../../lib/plainLanguage";
import { fmtDate, SegmentedFilter, Section } from "./primitives";
import AdminUploadButton from "./AdminUploadButton";
import DocReviewActions from "./DocReviewActions";
import InlineExtractionDetails from "./DocExtractionDetails";
import { ClaudeVerdictPill, DocStatePill, ExtractionPill } from "./DocPills";
import BundleInsight from "./BundleInsight";


export default function DocumentsTable({ docs, merchantId, onChanged }) {
  // Brief 3.10 — `onChanged` lets the parent refetch the unified merchant
  // view after a reject/withdraw/replacement action so the table reflects
  // the new state. Optional — callers that don't pass it just won't auto-
  // refresh after AM actions.
  const onChangedExternal = onChanged;
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [search, setSearch] = useState("");
  // Brief 3.5 PR 4 — single-row inline expansion. Replaces the old
  // modal drawer so operators don't lose their place on the page.
  // Only one row open at a time keeps the table scannable.
  const [expandedDocId, setExpandedDocId] = useState(null);
  // Per-row "downloading…" state so multiple in-flight requests don't blur.
  const [downloading, setDownloading] = useState({});

  const filtered = useMemo(() => {
    return (docs || []).filter((d) => {
      if (filterCategory !== "all" && d.category !== filterCategory) return false;
      if (filterState !== "all" && d.state !== filterState) return false;
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        return [d.filename, d.doc_key, d.doc_type_classified].some(
          (v) => String(v || "").toLowerCase().includes(q),
        );
      }
      return true;
    });
  }, [docs, filterCategory, filterState, search]);

  const onViewDocument = async (d) => {
    // Brief 3.6.1 — admin reads merchant's actual file to verify what
    // the cross-check engine extracted. Fetched as a blob (with auth
    // headers) and opened in a new tab as an object URL — bypasses the
    // browser's "window.open() can't pass Authorization headers" limit.
    const path = d.category === "financial"
      ? `/admin/merchants/${merchantId}/reports/${d.doc_id}/file`
      : `/admin/merchants/${merchantId}/documents/${d.doc_id}/file`;
    setDownloading((s) => ({ ...s, [d.doc_id]: true }));
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocked — fall back to a download link.
        const a = document.createElement("a");
        a.href = url;
        a.download = d.filename || d.doc_key || "document";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke after 60s — long enough for the new tab's PDF viewer to
      // grab the bytes; leaks the URL for at most a minute.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const status = err?.response?.status;
      alert(
        status === 404 ? "File not found — it may have been removed." :
        status === 403 ? "Permission denied." :
        "Couldn't open the document. Check the audit log."
      );
    } finally {
      setDownloading((s) => {
        const { [d.doc_id]: _, ...rest } = s;
        return rest;
      });
    }
  };

  const toggleExpand = (docId, canExpand) => {
    if (!canExpand) return;
    setExpandedDocId((cur) => (cur === docId ? null : docId));
  };

  return (
    <Section icon={FileText} title="Documents" testId="merchant-section-documents" subtitle={`${(docs || []).length} total`}>
      <div className="flex flex-wrap gap-3 mb-4">
        <SegmentedFilter
          value={filterCategory}
          options={[
            { key: "all", label: "All" },
            { key: "kyb", label: "KYB" },
            { key: "financial", label: "Financial" },
          ]}
          onChange={setFilterCategory}
          testId="docs-filter-category"
        />
        <SegmentedFilter
          value={filterState}
          options={[
            { key: "all", label: "Any state" },
            { key: "uploaded", label: "Uploaded" },
            { key: "na", label: "N/A" },
            { key: "pending", label: "Pending" },
          ]}
          onChange={setFilterState}
          testId="docs-filter-state"
        />
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter…"
            data-testid="docs-search"
            className="w-full pl-9 pr-3 py-1.5 rounded-full border border-white/[0.06] bg-white/[0.015] focus:border-[#00E5FF]/40 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 text-[12.5px] text-white placeholder:text-zinc-600"
          />
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.06] overflow-x-auto">
        <table className="w-full" data-testid="docs-table">
          <thead className="bg-white/[0.02] text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
            <tr>
              <th className="w-6 px-2 py-2.5" aria-hidden="true"></th>
              <th className="text-left px-4 py-2.5 font-normal">Document</th>
              <th className="text-left px-4 py-2.5 font-normal">Category</th>
              <th className="text-left px-4 py-2.5 font-normal">State</th>
              <th className="text-left px-4 py-2.5 font-normal">Check</th>
              <th className="text-left px-4 py-2.5 font-normal">Uploaded</th>
              <th className="text-right px-4 py-2.5 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody className="text-[12.5px]">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500" data-testid="docs-empty">
                No documents match these filters.
              </td></tr>
            )}
            {filtered.map((d) => {
              const canViewFile = d.state === "uploaded" && d.doc_id;
              const canViewExtraction = d.category === "kyb" && d.extraction_status && d.doc_id;
              const isDownloading = !!downloading[d.doc_id];
              const isExpanded = expandedDocId === d.doc_id;
              const sevPlain = plainFindingSeverity(d.finding_severity);
              const rowKey = `${d.category}-${d.doc_id || d.doc_key}`;
              return (
              <React.Fragment key={rowKey}>
              <tr
                className={`border-t border-white/[0.04] transition-colors ${
                  canViewExtraction
                    ? "cursor-pointer hover:bg-white/[0.02]"
                    : ""
                } ${isExpanded ? "bg-white/[0.02]" : ""}`}
                data-testid={`doc-row-${d.doc_id || d.doc_key}`}
                data-expanded={isExpanded ? "true" : "false"}
                onClick={() => toggleExpand(d.doc_id, canViewExtraction)}
              >
                <td className="w-6 px-2 py-3 align-top">
                  {canViewExtraction ? (
                    <ChevronRight
                      className={`h-3.5 w-3.5 text-zinc-500 transition-transform ${isExpanded ? "rotate-90 text-white" : ""}`}
                      data-testid={`doc-row-chevron-${d.doc_id || d.doc_key}`}
                      aria-label={isExpanded ? "Collapse details" : "Expand details"}
                    />
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <div className="text-white truncate max-w-[300px]">{d.filename || plainDocKey(d.doc_key)}</div>
                  <div className="text-zinc-500 text-[11px] mt-0.5">
                    {plainDocKey(d.doc_key)}{d.version ? ` · v${d.version}` : ""}
                    {d.is_bundle_source && d.bundle_member_count > 1 && (
                      <>
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[#00E5FF]"
                          title={
                            d.bundle_page_range && (d.bundle_page_range.first || d.bundle_page_range.last)
                              ? `Pages ${d.bundle_page_range.first ?? "?"}-${d.bundle_page_range.last ?? "?"} of the uploaded bundle PDF serve this slot. Classified ${d.bundle_classified_at ? new Date(d.bundle_classified_at).toLocaleString() : "recently"} by Claude.`
                              : "This file is uploaded to multiple slots. Claude is identifying which pages serve this slot — refresh in ~10s."
                          }
                          data-testid={`doc-bundle-badge-${d.doc_id || d.doc_key}`}
                        >
                          Bundle ({d.bundle_member_count} slots)
                          {d.bundle_page_range && (d.bundle_page_range.first || d.bundle_page_range.last) && (
                            <span className="text-zinc-500 text-[10.5px] font-mono">
                              · pp.{d.bundle_page_range.first ?? "?"}–{d.bundle_page_range.last ?? "?"}
                            </span>
                          )}
                        </span>
                        {d.file_hash && d.bundle_classified_at && (
                          <BundleInsight
                            merchantId={merchantId}
                            fileHash={d.file_hash}
                          />
                        )}
                      </>
                    )}
                    {d.ubos_were_derived && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-violet-300"
                        title={
                          d.derivation_rule ||
                          "UBOs were computed from the shareholder list using the Cyprus / EU 25% rule."
                        }
                        data-testid={`doc-ubo-derived-badge-${d.doc_id || d.doc_key}`}
                      >
                        UBOs derived
                      </span>
                    )}
                    {sevPlain && (
                      <span className={`ml-2 inline-flex items-center gap-1 ${
                        sevPlain.tone === "bad" ? "text-rose-300" :
                        sevPlain.tone === "warn" ? "text-amber-300" : "text-zinc-400"
                      }`}>
                        <AlertTriangle className="h-3 w-3" /> {sevPlain.label}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400">{d.category === "kyb" ? "KYB" : "Financial"}</td>
                <td className="px-4 py-3"><DocStatePill state={d.state} /></td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1.5">
                    <ExtractionPill status={d.extraction_status} classified={d.doc_type_classified} expected={d.doc_key} />
                    {/* Brief 3.13 Phase 2 — Claude semantic verdict pill.
                        Shows beneath the rule-based extraction pill so AMs see
                        both signals (extractor + Claude semantic) at a glance.
                        Hidden once the AM marks the row verified. */}
                    <ClaudeVerdictPill
                      verdict={d.claude_verdict}
                      findings={d.claude_findings}
                      manuallyVerified={d.extraction_status === "manually_verified"}
                      reviewing={
                        d.extraction_status === "success"
                        && !d.claude_verdict
                        && !!d.doc_id
                      }
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400 text-[11.5px]">
                  {fmtDate(d.uploaded_at)}
                  <div className="text-zinc-600">{d.uploaded_by}</div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      disabled={!canViewFile || isDownloading}
                      onClick={() => onViewDocument(d)}
                      data-testid={`doc-view-file-${d.doc_id || d.doc_key}`}
                      title={
                        !canViewFile ? "No file attached for this row"
                          : isDownloading ? "Opening…"
                          : "Open file (audit-logged)"
                      }
                      className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.02] px-2.5 py-1 text-[11px] text-zinc-300 hover:border-cyan-400/40 hover:bg-cyan-400/[0.06] hover:text-cyan-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-white/[0.08] disabled:hover:bg-white/[0.02] disabled:hover:text-zinc-300"
                    >
                      {isDownloading
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Eye className="h-3 w-3" />}
                      View file
                    </button>
                    {d.category === "kyb" && d.doc_key && (
                      <AdminUploadButton
                        merchantId={merchantId}
                        docKey={d.doc_key}
                        hasCurrent={canViewFile}
                        onUploaded={onChangedExternal}
                      />
                    )}
                    {canViewFile && (
                      <DocReviewActions
                        merchantId={merchantId}
                        doc={d}
                        onChanged={onChangedExternal}
                      />
                    )}
                  </div>
                </td>
              </tr>
              {isExpanded && canViewExtraction && (
                <tr
                  className="border-t border-white/[0.04] bg-white/[0.015]"
                  data-testid={`doc-row-expansion-${d.doc_id || d.doc_key}`}
                >
                  <td colSpan={7} className="px-6 py-5">
                    <InlineExtractionDetails
                      merchantId={merchantId}
                      doc={d}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            );})}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
